import { gql, useMutation, useQuery } from "@apollo/client";
import { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { FlatList, Keyboard, Platform, View } from "react-native";
import { ActivityIndicator, Text, ProgressBar } from "react-native-paper";
import { ScreenProps } from "../types";
import { colors } from "../../../../theme";

import {
  ActionsContainer,
  Button,
  ScreenContainer,
  ScrollableContainer,
  useErrorScroll,
  KeyboardAvoidingView,
} from "../../../components";
import TextInput from "../../../components/TextInput";
import { CurrentMember } from "../../fragments";
import config from "../../../config";
import SuccessModal from "./ProxySuccessModal";

export const PROXY_MUTATION = gql`
  mutation CreateCommerceProxy($input: CommerceCreateCommerceProxyInput!) {
    createCommerceProxy(input: $input) {
      id
    }
  }
`;

export const COMMERCE_ORDER_QUERY = gql`
  query DuesQuery {
    duesCycle: currentDuesCycle {
      id
      order {
        id
      }
    }
  }
`;

interface CommerceOrder {
  duesCycle: {
    order: {
      id: string;
    };
  };
}

export interface CommerceOrderQuery {
  currentOrder: CommerceOrder;
  currentMember: CurrentMember;
  duesCycle?: {
    id: string;
    order: {
      id: string;
    };
  };
}

type FormData = {
  proxyFirstName: string;
  proxyLastName: string;
  proxyEmailAddress: string;
  proxyEmailAddressConfirmation: string;
};

const defaultValues: FormData = {
  proxyFirstName: "",
  proxyLastName: "",
  proxyEmailAddress: "",
  proxyEmailAddressConfirmation: "",
};

const DEFAULT_ERROR = "An unknown error occurred while submitting this form. Please try again.";

export default function AssignProxyScreen({ navigation }: ScreenProps<"AssignProxy">) {
  const { data } = useQuery<CommerceOrderQuery>(COMMERCE_ORDER_QUERY);
  const [sendMutation] = useMutation(PROXY_MUTATION);
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<FormData>({ defaultValues, mode: "onSubmit" });

  const [apiError, setApiError] = useState("");
  const [successfullyAssignedProxy, setSuccessfullyAssignedProxy] = useState(false);
  const flatListRef = useRef<FlatList>(null);
  const { onSubmitError, scrollToField } = useErrorScroll({ defaultValues, flatListRef });

  const onSubmit = async (formData: FormData) => {
    Keyboard.dismiss();
    const commerceOrderId = data?.duesCycle?.order.id;

    try {
      const {
        data: {},
      } = await sendMutation({
        variables: {
          input: {
            firstName: formData.proxyFirstName,
            lastName: formData.proxyLastName,
            emailAddress: formData.proxyEmailAddress,
            commerceOrderId: commerceOrderId,
          },
        },
      });
      setSuccessfullyAssignedProxy(true);
    } catch (e: any) {
      const message = e && e.message ? e.message : DEFAULT_ERROR;
      setApiError(message);
    }
  };

  if (!data?.duesCycle?.order) {
    return (
      <View className="flex-1 bg-sky-white items-center justify-center">
        <ActivityIndicator />
      </View>
    );
  }

  return (
    <ScreenContainer navigationTitle="Assign Proxy">
      {Platform.OS !== "web" && <ProgressBar progress={0.75} color={colors.primary.light} />}
      <KeyboardAvoidingView>
        <ScrollableContainer containerClassName="bg-white" testID="assign-proxy-screen" grow>
          <View className="m-6">
            <Text className="text-large-normal font-700 text-ink-darkest pb-4">Proxy Information</Text>
            <Text className="text-small-normal font-400 text-ink -mb-4">
              An email with instructions and a link to your dues summary will be sent to your designated proxy.
            </Text>
          </View>

          <View className="space-y-2 mx-6 mb-4">
            <View>
              <Controller
                name="proxyFirstName"
                control={control}
                rules={{
                  required: "First name is required",
                }}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <TextInput
                    error={error}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    value={value}
                    label="Proxy First Name"
                    testID="proxy-first-name-input"
                    onFocus={() => {
                      scrollToField("proxyFirstName");
                    }}
                  />
                )}
              />
            </View>

            <View>
              <Controller
                name="proxyLastName"
                control={control}
                rules={{
                  required: "Last name is required",
                }}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <TextInput
                    error={error}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    value={value}
                    label="Proxy Last Name"
                    testID="proxy-last-name-input"
                    onFocus={() => {
                      scrollToField("proxyLastName");
                    }}
                  />
                )}
              />
            </View>

            <View>
              <Controller
                name="proxyEmailAddress"
                control={control}
                rules={{
                  required: "Email Address is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Please enter a valid email address",
                  },
                }}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <TextInput
                    error={error}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    value={value}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    label="Proxy Email Address"
                    testID="proxy-email-address-input"
                    onFocus={() => {
                      scrollToField("proxyEmailAddress");
                    }}
                  />
                )}
              />
            </View>

            <View>
              <Controller
                name="proxyEmailAddressConfirmation"
                control={control}
                rules={{
                  required: "Email Address Confirmation is required",
                  validate: (value, { proxyEmailAddress }) =>
                    proxyEmailAddress === value ? true : "Email addresses do not match",
                }}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <TextInput
                    error={error}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    value={value}
                    keyboardType="email-address"
                    autoCapitalize="none"
                    label="Confirm Proxy Email Address"
                    testID="proxy-email-address-confirmation-input"
                    onFocus={() => {
                      scrollToField("proxyEmailAddressConfirmation");
                    }}
                  />
                )}
              />
            </View>
          </View>

          <View className="pt-6 mt-auto">
            <ActionsContainer errorAlert errorMessage={apiError} onCloseErrorMessage={setApiError}>
              <Button
                loading={isSubmitting}
                disabled={isSubmitting}
                isValid={isValid}
                onPress={handleSubmit(onSubmit, onSubmitError)}
                mode="contained"
              >
                Send
              </Button>
            </ActionsContainer>
          </View>
        </ScrollableContainer>
      </KeyboardAvoidingView>

      <SuccessModal
        visible={successfullyAssignedProxy}
        onClose={() => {
          if (Platform.OS === "web") {
            window.location.assign(`https://${config.accountDomain}`);
          } else {
            navigation.getParent()?.goBack();
          }
        }}
      />
    </ScreenContainer>
  );
}
